import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getCountryCallingCode } from 'react-phone-number-input/max'
import { useFormatMessage } from '@/translations/useFormatMessage'
import {
	StyledCountrySelectWrapper,
	StyledTriggerButton,
	StyledCallingCode,
	StyledArrow,
	StyledDropdown,
	StyledSearchInput,
	StyledOptionsList,
	StyledOptionItem,
	StyledOptionLabel,
	StyledOptionCode,
	StyledNoResults,
} from './CountrySearchSelect.styled'
import type { Country } from 'react-phone-number-input/max'
import type { CountrySearchSelectProps, CountrySelectOption } from './CountrySearchSelect.types'

const LISTBOX_ID = 'signin-country-listbox'

type CountryOption = CountrySelectOption & { value: Country }

function optionId(country: Country): string {
	return `signin-country-option-${country}`
}

function matchesSearchTerm(option: CountryOption, term: string): boolean {
	if (option.label.toLowerCase().includes(term)) return true
	const digits = term.replace(/^\+/, '')
	return /^\d+$/.test(digits) && getCountryCallingCode(option.value).startsWith(digits)
}

export function CountrySearchSelect({
	value,
	onChange,
	options,
	iconComponent: Icon,
	disabled,
	readOnly,
}: CountrySearchSelectProps) {
	const { t } = useFormatMessage()
	const [isOpen, setIsOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [activeIndex, setActiveIndex] = useState(-1)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLUListElement>(null)

	const countryOptions = useMemo(
		() =>
			options.filter((option): option is CountryOption => !option.divider && !!option.value),
		[options],
	)

	const term = searchTerm.trim().toLowerCase()
	const filteredOptions = term
		? countryOptions.filter((option) => matchesSearchTerm(option, term))
		: countryOptions

	const closeDropdown = useCallback(() => {
		setIsOpen(false)
		setSearchTerm('')
		setActiveIndex(-1)
	}, [])

	useEffect(() => {
		if (isOpen) {
			searchInputRef.current?.focus()
		}
	}, [isOpen])

	useEffect(() => {
		if (activeIndex < 0 || !listRef.current) return
		const activeEl = listRef.current.querySelector<HTMLElement>(
			`#${optionId(filteredOptions[activeIndex].value)}`,
		)
		activeEl?.scrollIntoView?.({ block: 'nearest' })
	}, [activeIndex, filteredOptions])

	useEffect(() => {
		if (!isOpen) return

		function handleClickOutside(event: MouseEvent) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				closeDropdown()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen, closeDropdown])

	const selectCountry = (country: Country) => {
		closeDropdown()
		// The library moves focus to the phone input after this
		// (focusInputOnCountrySelection defaults to true).
		onChange(country)
	}

	const handleTriggerClick = () => {
		if (readOnly) return
		if (isOpen) {
			closeDropdown()
		} else {
			setIsOpen(true)
		}
	}

	const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (readOnly) return
		if (event.key === 'ArrowDown' && !isOpen) {
			event.preventDefault()
			setIsOpen(true)
		}
	}

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value)
		setActiveIndex(event.target.value.trim() ? 0 : -1)
	}

	const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1))
				break
			case 'ArrowUp':
				event.preventDefault()
				setActiveIndex((prev) => Math.max(prev - 1, -1))
				break
			case 'Enter':
				event.preventDefault()
				if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
					selectCountry(filteredOptions[activeIndex].value)
				}
				break
			case 'Escape':
				// Close only the dropdown — without this the modal's Escape handler
				// would close the whole sign-in modal.
				event.preventDefault()
				event.stopPropagation()
				closeDropdown()
				triggerRef.current?.focus()
				break
			case 'Tab':
				closeDropdown()
				break
		}
	}

	const selectedOption = countryOptions.find((option) => option.value === value)

	return (
		<StyledCountrySelectWrapper ref={wrapperRef}>
			<StyledTriggerButton
				ref={triggerRef}
				type="button"
				onClick={handleTriggerClick}
				onKeyDown={handleTriggerKeyDown}
				disabled={disabled}
				$readOnly={readOnly}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-label={
					readOnly && value
						? `${t('signin:signInForm:countrySelectAriaLabel')} +${getCountryCallingCode(value)}, read-only`
						: t('signin:signInForm:countrySelectAriaLabel')
				}
				data-testid="country-selector-trigger"
			>
				{value && (
					<Icon country={value} label={selectedOption?.label ?? value} aria-hidden />
				)}
				{value && (
					<StyledCallingCode>{`+${getCountryCallingCode(value)}`}</StyledCallingCode>
				)}
				{!readOnly && <StyledArrow aria-hidden />}
			</StyledTriggerButton>

			{isOpen && (
				<StyledDropdown>
					<StyledSearchInput
						ref={searchInputRef}
						type="text"
						role="combobox"
						aria-autocomplete="list"
						aria-expanded={isOpen}
						aria-controls={LISTBOX_ID}
						aria-activedescendant={
							activeIndex >= 0 && activeIndex < filteredOptions.length
								? optionId(filteredOptions[activeIndex].value)
								: undefined
						}
						placeholder={t('signin:signInForm:countrySearchPlaceholder')}
						aria-label={t('signin:signInForm:countrySearchPlaceholder')}
						value={searchTerm}
						onChange={handleSearchChange}
						onKeyDown={handleSearchKeyDown}
					/>

					{filteredOptions.length > 0 ? (
						<StyledOptionsList ref={listRef} id={LISTBOX_ID} role="listbox">
							{filteredOptions.map((option, index) => (
								<StyledOptionItem
									key={option.value}
									id={optionId(option.value)}
									role="option"
									aria-selected={index === activeIndex}
									isActive={index === activeIndex}
									onClick={() => selectCountry(option.value)}
									onMouseEnter={() => setActiveIndex(index)}
								>
									<Icon country={option.value} label={option.label} aria-hidden />
									<StyledOptionLabel>{option.label}</StyledOptionLabel>
									<StyledOptionCode>{`+${getCountryCallingCode(option.value)}`}</StyledOptionCode>
								</StyledOptionItem>
							))}
						</StyledOptionsList>
					) : (
						<StyledNoResults>{t('signin:signInForm:countryNoResults')}</StyledNoResults>
					)}
				</StyledDropdown>
			)}
		</StyledCountrySelectWrapper>
	)
}
